import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Printer, Users, RefreshCw, Edit, X as XIcon, Send, Check, Activity, KeyRound, BookOpen, Copy, Snowflake, PauseCircle, PlayCircle } from "lucide-react";

const ClassRoster = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [nicknameDraft, setNicknameDraft] = useState("");
  const [showPush, setShowPush] = useState(false);
  const [pushForm, setPushForm] = useState({ topic: "", difficulty: "standard", override: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [freezeBusy, setFreezeBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/teacher/classes/${id}`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load class");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const regeneratePins = async () => {
    if (!window.confirm("Generate new PINs for every student in this class? All current PINs will stop working right away, and students will need their new PIN to log in.")) return;
    setRegenBusy(true);
    try {
      const r = await api.post(`/teacher/classes/${id}/regenerate-pins`);
      setData((d) => ({ ...d, roster: r.data.roster }));
      toast.success(`New PINs created for ${r.data.regenerated} students`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate new PINs");
    } finally {
      setRegenBusy(false);
    }
  };

  const copyPin = (pin) => {
    if (!pin) return;
    navigator.clipboard?.writeText(pin).then(
      () => toast.success(`PIN ${pin} copied`),
      () => toast.error("Could not copy")
    );
  };

  const saveNickname = async (slot) => {
    try {
      await api.put(`/teacher/classes/${id}/slots/${slot.student_id}`, { nickname: nicknameDraft.trim() });
      toast.success(nicknameDraft.trim() ? "Slot assigned" : "Slot unassigned & progress cleared");
      setEditingSlot(null);
      setNicknameDraft("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const unassignSlot = async (slot) => {
    if (!window.confirm(`Unassign ${slot.username}? Their progress will be permanently cleared and PIN regenerated.`)) return;
    try {
      const r = await api.put(`/teacher/classes/${id}/slots/${slot.student_id}`, { nickname: null });
      toast.success(`Reassigned as ${r.data.username} · new PIN ${r.data.pin}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not unassign");
    }
  };

  const resetPin = async (slot) => {
    if (!window.confirm(`Give ${slot.username} a new PIN? Their current PIN will stop working.`)) return;
    try {
      const r = await api.post(`/teacher/classes/${id}/slots/${slot.student_id}/reset-pin`);
      setData((d) => ({ ...d, roster: d.roster.map((s) => (s.student_id === slot.student_id ? { ...s, pin: r.data.pin } : s)) }));
      toast.success(`New PIN for ${r.data.username}: ${r.data.pin}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset PIN");
    }
  };

  const download = async (kind) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/teacher/classes/${id}/${kind === "cards" ? "print-cards" : "print-roster"}`;
      const win = window.open(url, "_blank");
      if (!win) toast.error("Popup blocked — please allow pop-ups for this site.");
      else {
        win.opener = null;
        toast.success(`Downloading ${kind === "cards" ? "login cards" : "roster"} PDF`);
      }
    } catch (err) {
      toast.error("Could not open PDF");
    }
  };

  const pushExpedition = async (e) => {
    e.preventDefault();
    if (!pushForm.topic.trim()) { toast.error("Enter a topic"); return; }
    setPushBusy(true);
    try {
      const r = await api.post(`/teacher/classes/${id}/expeditions`, pushForm);
      const paused = r.data.paused_count || 0;
      toast.success(
        paused > 0
          ? `Pushed to ${r.data.created_count} climbers · paused ${paused} in-progress expedition${paused === 1 ? "" : "s"}`
          : `Pushed to ${r.data.created_count} assigned climbers`
      );
      setShowPush(false);
      setPushForm({ topic: "", difficulty: "standard", override: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not push");
    } finally {
      setPushBusy(false);
    }
  };

  const toggleFreeze = async () => {
    const currentlyFrozen = !!data?.class?.frozen;
    const nextFrozen = !currentlyFrozen;
    const confirmMsg = nextFrozen
      ? "Freeze the class? Every student will be stopped where they are and can't submit work until you unfreeze."
      : "Unfreeze the class? Students will be able to work again immediately.";
    if (!window.confirm(confirmMsg)) return;
    setFreezeBusy(true);
    try {
      await api.post(`/teacher/classes/${id}/freeze`, { frozen: nextFrozen });
      toast.success(nextFrozen ? "Class frozen — students paused" : "Class unfrozen — students can climb again");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update freeze");
    } finally {
      setFreezeBusy(false);
    }
  };

  if (!data) return <div className="min-h-screen bg-mm-parchment"><Navbar /><div className="text-center py-20 text-mm-navy/60">Loading roster...</div></div>;

  const cls = data.class;
  const roster = data.roster || [];
  const assigned = roster.filter((r) => r.assigned).length;
  const guideUrl = `${window.location.origin}/student/login-guide`;

  const copyGuideUrl = () => {
    navigator.clipboard?.writeText(guideUrl).then(
      () => toast.success("Student guide link copied"),
      () => toast.error("Could not copy")
    );
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10 max-w-6xl">
        <Link to="/teacher/dashboard" className="text-sm text-mm-steel font-bold hover:text-mm-navy">← Back to HQ</Link>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Class roster</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy mt-2" data-testid="class-roster-title">{cls.name}</h1>
            <p className="text-mm-navy/70 mt-1 text-sm">
              {cls.subject || "General"}{cls.grade_band ? ` · Grade ${cls.grade_band}` : ""} · {assigned}/30 assigned
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={toggleFreeze}
              disabled={freezeBusy}
              className={`btn-trail text-sm disabled:opacity-50 ${cls.frozen ? "btn-gold" : "btn-outline-navy"}`}
              data-testid="freeze-class-btn"
            >
              {cls.frozen ? <PlayCircle size={14} strokeWidth={2.5} /> : <PauseCircle size={14} strokeWidth={2.5} />}
              {freezeBusy ? "Updating..." : cls.frozen ? "Unfreeze class" : "Freeze class"}
            </button>
            <Link to="/student/login-guide" target="_blank" rel="noopener" className="btn-trail btn-outline-navy text-sm" data-testid="student-guide-link">
              <BookOpen size={14} strokeWidth={2.5} /> Student login guide
            </Link>
            <Link to={`/teacher/classes/${id}/live`} className="btn-trail btn-outline-navy text-sm" data-testid="live-monitor-link">
              <Activity size={14} strokeWidth={2.5} /> Live monitor
            </Link>
            <button onClick={regeneratePins} disabled={regenBusy} className="btn-trail btn-outline-navy text-sm disabled:opacity-50" data-testid="regenerate-pins-btn">
              <KeyRound size={14} strokeWidth={2.5} />
              {regenBusy ? "Generating..." : "Generate New PINs"}
            </button>
            <button onClick={() => download("cards")} className="btn-trail btn-outline-navy text-sm" data-testid="print-cards-btn">
              <Printer size={14} strokeWidth={2.5} /> Print Login Cards
            </button>
            <button onClick={() => download("roster")} className="btn-trail btn-outline-navy text-sm" data-testid="print-roster-btn">
              <Printer size={14} strokeWidth={2.5} /> Print Roster
            </button>
            <button onClick={() => setShowPush(true)} className="btn-trail btn-gold text-sm" data-testid="push-expedition-btn">
              <Send size={14} strokeWidth={2.5} /> Push expedition
            </button>
          </div>
        </div>

        {/* Frozen banner */}
        {cls.frozen && (
          <div className="mt-4 rounded-xl border-2 border-sky-400 bg-sky-100 p-4 flex items-center gap-3" data-testid="frozen-banner">
            <div className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center shrink-0">
              <Snowflake size={20} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="font-heading font-black text-sky-900">Class is frozen</div>
              <p className="text-sm text-sky-900/80 mt-0.5">
                Every student is paused where they are — they can't submit work, complete stages, or start new expeditions until you unfreeze the class.
              </p>
            </div>
          </div>
        )}

        {/* Share student guide banner */}
        <div className="mt-4 rounded-xl border-2 border-mm-gold/40 bg-mm-gold/10 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="share-guide-banner">
          <div className="flex items-center gap-2 text-sm text-mm-navy">
            <BookOpen size={16} strokeWidth={2.5} className="text-mm-navy shrink-0" />
            <span><b>Share with students:</b> <span className="font-mono text-xs bg-white px-2 py-1 rounded border border-mm-navy/10">{guideUrl}</span></span>
          </div>
          <button onClick={copyGuideUrl} className="btn-trail btn-outline-navy text-xs" data-testid="copy-guide-link-btn">
            <Copy size={12} strokeWidth={2.5} /> Copy link
          </button>
        </div>

        <div className="mt-4 rounded-xl border-2 border-mm-steel/20 bg-white p-3 text-xs text-mm-navy/80">
          <b>PINs stay the same</b> until you change them. Printing cards or the roster never changes PINs. Use <b>Generate New PINs</b> to change every PIN in the class, or the <RefreshCw size={11} className="inline -mt-0.5" /> button to change one student's PIN.
        </div>

        <div className="mt-6 rounded-xl bg-white border-2 border-mm-navy/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="class-roster-table">
            <thead className="bg-mm-parchment">
              <tr className="text-left text-xs uppercase tracking-widest font-bold text-mm-steel">
                <th className="p-3">Slot</th>
                <th className="p-3">Username</th>
                <th className="p-3">PIN</th>
                <th className="p-3">Assigned to</th>
                <th className="p-3">Last active</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roster.map((slot) => {
                const pin = slot.pin;
                return (
                  <tr key={slot.student_id} className="border-t border-mm-navy/10" data-testid={`roster-row-${slot.slot_number}`}>
                    <td className="p-3 font-bold text-mm-navy">Slot {slot.slot_number}</td>
                    <td className="p-3 font-heading font-bold text-mm-navy">{slot.username}</td>
                    <td className="p-3">
                      <button
                          onClick={() => copyPin(pin)}
                          className="font-mono font-bold text-mm-navy text-base tracking-[0.2em] bg-mm-gold/15 hover:bg-mm-gold/25 px-2.5 py-1 rounded border-2 border-mm-gold/40 transition"
                          title="Click to copy PIN"
                          data-testid={`pin-value-${slot.slot_number}`}
                        >
                          {pin}
                        </button>
                    </td>
                    <td className="p-3">
                      {editingSlot === slot.student_id ? (
                        <div className="flex gap-1">
                          <input
                            value={nicknameDraft}
                            onChange={(e) => setNicknameDraft(e.target.value)}
                            autoFocus
                            placeholder="Front row left..."
                            className="border-2 border-mm-navy/15 rounded px-2 py-1 text-sm w-40"
                            data-testid={`nickname-input-${slot.slot_number}`}
                          />
                          <button onClick={() => saveNickname(slot)} className="p-1 text-emerald-700" data-testid={`nickname-save-${slot.slot_number}`}><Check size={16} /></button>
                          <button onClick={() => { setEditingSlot(null); setNicknameDraft(""); }} className="p-1 text-mm-navy/50"><XIcon size={16} /></button>
                        </div>
                      ) : slot.assigned ? (
                        <span className="text-mm-navy" data-testid={`nickname-${slot.slot_number}`}>{slot.nickname}</span>
                      ) : (
                        <span className="text-mm-navy/40 italic">— unassigned —</span>
                      )}
                    </td>
                    <td className="p-3 text-mm-navy/70">{slot.last_active_at || "—"}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setEditingSlot(slot.student_id); setNicknameDraft(slot.nickname || ""); }}
                        className="text-mm-steel hover:text-mm-navy mr-2"
                        title="Edit nickname"
                        data-testid={`edit-nickname-btn-${slot.slot_number}`}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => resetPin(slot)}
                        className="text-mm-steel hover:text-mm-navy mr-2"
                        title="Give this student a new PIN"
                        data-testid={`reset-pin-btn-${slot.slot_number}`}
                      >
                        <RefreshCw size={14} />
                      </button>
                      {slot.assigned && (
                        <button
                          onClick={() => unassignSlot(slot)}
                          className="text-red-600 hover:text-red-800"
                          title="Unassign & clear progress"
                          data-testid={`unassign-btn-${slot.slot_number}`}
                        >
                          <XIcon size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-xs text-mm-navy/60 flex items-center gap-1">
          <Users size={12} /> Nicknames are stored only in your teacher view — never on the student accounts themselves.
        </div>
      </div>

      {showPush && (
        <div className="fixed inset-0 z-50 bg-mm-navy/50 flex items-center justify-center p-4" onClick={() => setShowPush(false)}>
          <form
            onSubmit={pushExpedition}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl p-6 border-2 border-mm-navy/10 space-y-3"
            data-testid="push-expedition-form"
          >
            <h3 className="font-heading text-2xl font-black text-mm-navy">Push expedition to class</h3>
            <p className="text-xs text-mm-navy/70">One expedition per assigned climber. Unassigned slots are skipped.</p>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Topic</label>
              <input value={pushForm.topic} onChange={(e) => setPushForm({ ...pushForm, topic: e.target.value })}
                className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3"
                placeholder="Photosynthesis · Fractions · The Water Cycle..."
                data-testid="push-topic-input" />
              <p className="mt-2 text-xs text-mm-navy/70 leading-relaxed" data-testid="push-topic-hint">
                <b>Tip:</b> Specific topics work better than broad ones. Try <span className="font-mono text-mm-navy">Subatomic Particles</span> instead of <span className="font-mono text-mm-navy">The Atomic Theory</span>.
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Difficulty</label>
              <select value={pushForm.difficulty} onChange={(e) => setPushForm({ ...pushForm, difficulty: e.target.value })}
                className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3"
                data-testid="push-difficulty-select">
                <option value="standard">Standard trail</option>
                <option value="rescue">Rescue route (scaffolded)</option>
                <option value="explorer">Explorer's challenge</option>
              </select>
            </div>
            <label className="flex items-start gap-2 rounded-lg border-2 border-mm-navy/15 bg-mm-parchment p-3 cursor-pointer hover:border-mm-steel" data-testid="push-override-label">
              <input
                type="checkbox"
                checked={!!pushForm.override}
                onChange={(e) => setPushForm({ ...pushForm, override: e.target.checked })}
                className="mt-0.5 shrink-0 accent-mm-gold w-4 h-4"
                data-testid="push-override-checkbox"
              />
              <span className="text-sm text-mm-navy">
                <b>Override current work.</b> Pause any expeditions students are currently on so this new one takes priority.
              </span>
            </label>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowPush(false)} className="btn-trail btn-outline-navy text-sm flex-1">Cancel</button>
              <button type="submit" disabled={pushBusy} className="btn-trail btn-gold text-sm flex-1 disabled:opacity-50" data-testid="push-expedition-submit">
                {pushBusy ? "Pushing..." : "Push to class"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ClassRoster;
